import {useLayoutEffect, useRef, useState} from 'react';

/** User navigation pauses following synchronously, before queued resize callbacks. */
export function useChatFollow(active: boolean, content: readonly unknown[]) {
  const body = useRef<HTMLDivElement>(null);
  const following = useRef(true);
  const previousTop = useRef(0);
  const touchY = useRef<number | null>(null);
  const [follow, setFollow] = useState(true);
  function pause() {
    if (!active) return;
    following.current = false;
    setFollow(false);
  }
  function latest() {
    following.current = true;
    setFollow(true);
    if (body.current) {
      body.current.scrollTop = body.current.scrollHeight;
      previousTop.current = body.current.scrollTop;
    }
  }
  useLayoutEffect(() => {
    const container = body.current;
    if (!active || !container) return;
    const scroll = () => {
      if (!following.current) return;
      container.scrollTop = container.scrollHeight;
      previousTop.current = container.scrollTop;
    };
    scroll();
    const observer = new ResizeObserver(scroll);
    observer.observe(container);
    for (const child of container.children) observer.observe(child);
    return () => observer.disconnect();
  }, [active, follow, ...content]);
  return {body, follow, latest, pause, handlers: {
    onClickCapture(event: React.MouseEvent<HTMLDivElement>) {
      if (!event.isTrusted || !(event.target instanceof Element)) return;
      const details = event.target.closest('summary')?.parentElement;
      // Opening recorded details is a reading action. Pause before the native
      // toggle changes their height, rather than jumping past the new content.
      if (details instanceof HTMLDetailsElement && !details.open) pause();
    },
    onScroll() {
      const container = body.current;
      if (!active || !container) return;
      // Covers scrollbar dragging and accessibility scrolling as well as touch.
      const atBottom = container.scrollHeight - container.clientHeight - container.scrollTop <= 2;
      if (!atBottom && container.scrollTop < previousTop.current - 1) pause();
      previousTop.current = container.scrollTop;
    },
    onWheel(event: React.WheelEvent<HTMLDivElement>) {
      if (event.deltaY < 0) pause();
    },
    onTouchStart(event: React.TouchEvent<HTMLDivElement>) {
      touchY.current = event.touches[0]?.clientY ?? null;
    },
    onTouchMove(event: React.TouchEvent<HTMLDivElement>) {
      const y = event.touches[0]?.clientY;
      if (y !== undefined && touchY.current !== null && Math.abs(y - touchY.current) > 3) pause();
    },
    onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
      if ((event.target as HTMLElement).closest('input, textarea, select, [contenteditable="true"]')) return;
      if (['ArrowUp', 'PageUp', 'Home'].includes(event.key) || event.key === ' ' && event.shiftKey) pause();
    }
  }};
}
